export const AUDIO_METADATA_TIMEOUT_MS = 12_000;

interface Options {
  signal?: AbortSignal;
  timeoutMs?: number;
}

const metadataError = (code: 'AUDIO_METADATA_ERROR' | 'AUDIO_METADATA_TIMEOUT' | 'EXAM_ABORTED') => (
  Object.assign(new Error('Listening audio metadata could not be loaded'), {code})
);

function fetchAudioDuration(url: string, signal: AbortSignal, timeoutMs: number): Promise<number> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(metadataError('EXAM_ABORTED'));
      return;
    }

    const audio = document.createElement('audio');
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      const duration = audio.duration;
      clearTimeout(timer);
      audio.removeEventListener('loadedmetadata', loaded);
      audio.removeEventListener('error', failed);
      signal.removeEventListener('abort', aborted);
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      if (error) reject(error);
      else resolve(duration);
    };
    const loaded = () => finish(Number.isFinite(audio.duration) && audio.duration > 0
      ? undefined : metadataError('AUDIO_METADATA_ERROR'));
    const failed = () => finish(metadataError('AUDIO_METADATA_ERROR'));
    const aborted = () => finish(metadataError('EXAM_ABORTED'));

    audio.addEventListener('loadedmetadata', loaded);
    audio.addEventListener('error', failed);
    signal.addEventListener('abort', aborted, {once: true});
    const timer = setTimeout(() => finish(metadataError('AUDIO_METADATA_TIMEOUT')), timeoutMs);
    try {
      if (!url || !audio.canPlayType) throw metadataError('AUDIO_METADATA_ERROR');
      audio.preload = 'metadata';
      audio.src = url;
    } catch {
      failed();
    }
  });
}

export async function getTotalAudioDuration(audioUrls: string[], options: Options = {}): Promise<number> {
  if (options.signal?.aborted) throw metadataError('EXAM_ABORTED');
  if (!audioUrls?.length) return 0;

  const request = new AbortController();
  const abort = () => request.abort();
  options.signal?.addEventListener('abort', abort, {once: true});
  try {
    const durations = await Promise.all(audioUrls.map(url => fetchAudioDuration(
      url, request.signal, options.timeoutMs ?? AUDIO_METADATA_TIMEOUT_MS,
    )));
    return durations.reduce((total, duration) => total + duration, 0);
  } finally {
    // Release remaining media requests when any Part fails or loading is cancelled.
    request.abort();
    options.signal?.removeEventListener('abort', abort);
  }
}
